import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ChevronLeft, ChevronRight, Flag, MapPin, Share2 } from "lucide-react";
import { errMessage } from "../lib/api";
import { loadProposal } from "../lib/prefetch";
import { fmtDate, fmtShort } from "../lib/format";
import { useIsMobile } from "../hooks/useIsMobile";
import { SiteFooter, SiteHeader } from "../components/SiteHeader";
import { Avatar, Loader, StatusBadge } from "../components/Bits";
import { HeaderMedia } from "../components/HeaderMedia";
import { Gallery } from "../components/Gallery";
import { StoryHtml } from "../components/StoryHtml";
import { MobileVoteBar, VoteCard, share, useVote } from "../components/VoteCard";
import { MiniMap } from "../components/LazyMaps";
import { Logo } from "../components/Logo";

const MobileTop = ({ p }) => {
  const navigate = useNavigate();
  return (
    <header data-testid="proposal-mobile-header" style={{ height: 56, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 6px", background: "#fff", borderBottom: "1px solid var(--border)" }}>
      <button type="button" className="icon-btn" style={{ border: "none", background: "transparent" }} aria-label="Back"
        onClick={() => (window.history.length > 1 ? navigate(-1) : navigate("/"))} data-testid="proposal-back-btn"><ChevronLeft size={24} /></button>
      <Link to="/" aria-label="ReimagineCork home" style={{ textDecoration: "none" }}><Logo size={30} text={19} /></Link>
      {p ? <button type="button" className="icon-btn" style={{ border: "none", background: "transparent" }} aria-label="Share this proposal" onClick={() => share(p)} data-testid="proposal-share-top-btn"><Share2 size={21} /></button>
        : <span style={{ width: 44 }} />}
    </header>
  );
};

const Crumbs = ({ p }) => (
  <nav aria-label="Breadcrumb" style={{ fontSize: 14, color: "var(--muted)", display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap", padding: "20px 0" }}>
    <Link to="/" style={{ color: "var(--muted)" }}>Map</Link><ChevronRight size={14} aria-hidden="true" />
    {p.area && p.area !== p.place && <><Link to={`/?area=${encodeURIComponent(p.area)}`} style={{ color: "var(--muted)" }}>{p.area}</Link><ChevronRight size={14} aria-hidden="true" /></>}
    <span aria-current="page" style={{ color: "var(--ink-2)", fontWeight: 600 }}>{p.place}</span>
  </nav>
);

const PreviewBanner = ({ p }) => (
  <div role="status" data-testid="preview-banner" style={{ background: "#fff4d6", color: "#6b4a00", borderBottom: "1px solid #f1dfa6" }}>
    <div className="wrap-md" style={{ minHeight: 52, display: "flex", flexWrap: "wrap", gap: "6px 14px", alignItems: "center", fontSize: 15 }}>
      <strong>Preview.</strong> Only you and our team can see this page. <StatusBadge status={p.status} small />
      {p.status === "draft" && <Link to={`/builder/${p.id}`} style={{ color: "#6b4a00", fontWeight: 700 }} data-testid="preview-back-to-editor">Back to editing</Link>}
    </div>
  </div>
);

const Aside = ({ p, v, mobile }) => (
  <aside style={{ flex: "1 1 320px", minWidth: 0, display: "flex", flexDirection: "column", gap: 20, position: mobile ? "static" : "sticky", top: 24 }}>
    {!mobile && !p.preview && <VoteCard p={p} v={v} />}
    {p.preview && (
      <div className="box"><p className="hint">Voting opens when the proposal goes live.</p></div>
    )}
    {p.lat != null && (
      <section className="box" aria-labelledby="h-where" style={{ padding: 0, overflow: "hidden" }}>
        <MiniMap lat={p.lat} lng={p.lng} height={180} />
        <div style={{ padding: 18 }}>
          <h2 id="h-where" style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)", margin: "0 0 4px" }}>Location</h2>
          <p style={{ margin: "0 0 8px", fontSize: 16, fontWeight: 600, display: "flex", gap: 6, alignItems: "center" }}><MapPin size={16} color="#C8102E" />{p.location_name || p.place}</p>
          {!p.preview && <Link to={`/?focus=${p.id}`} style={{ fontWeight: 600, fontSize: 15 }} data-testid="view-on-map-link">View on the map</Link>}
        </div>
      </section>
    )}
    {!p.preview && (
      <section className="box" aria-labelledby="h-status">
        <h2 id="h-status" style={{ fontSize: 14, fontWeight: 600, color: "var(--muted)", margin: "0 0 8px" }}>Status</h2>
        <StatusBadge status="live" />
        <p className="hint" style={{ marginTop: 10 }}>Reviewed and approved by the ReimagineCork team on {fmtDate(p.published_at)}.</p>
        <Link to={`/report?proposal=${encodeURIComponent(p.title)}`} style={{ display: "inline-flex", alignItems: "center", gap: 6, marginTop: 12, fontSize: 14, color: "var(--muted)" }} data-testid="report-link">
          <Flag size={14} />Report this proposal
        </Link>
      </section>
    )}
  </aside>
);

const TopicTags = ({ p }) => (
  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
    {p.topic_items.map((t) => (
      <Link key={t.id} to={`/?topic=${t.id}`} className="topic" title={`See all ${t.name} proposals on the map`} data-testid={`proposal-topic-${t.id}`}>{t.name}</Link>
    ))}
  </div>
);

const Byline = ({ p, mobile }) => (
  <p style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px 16px", margin: "0 0 32px", fontSize: 15, color: "var(--body)" }}>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}><MapPin size={16} color="#C8102E" />{p.location_name || p.place}</span>
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}><Avatar text={p.author_initials || "FM"} size={30} />By <strong>{p.author_name}</strong>
      {mobile && p.published_at && <span className="meta" style={{ fontSize: 15 }}>· {fmtShort(p.published_at)}</span>}</span>
    {!mobile && p.published_at && <span className="meta" style={{ fontSize: 15 }}>Published {fmtDate(p.published_at)}</span>}
  </p>
);

const Article = ({ p, mobile }) => (
  <article style={{ flex: "999 1 560px", minWidth: 0, maxWidth: 760 }} className="rise">
    <TopicTags p={p} />
    <h1 className="head" style={{ fontSize: mobile ? 32 : 48, lineHeight: 1.05, letterSpacing: "-.03em", margin: "0 0 18px", fontWeight: 800 }} data-testid="proposal-title">{p.title}</h1>
    <Byline p={p} mobile={mobile} />
    <section aria-labelledby="h-summary" style={{ marginBottom: 40 }}>
      <h2 id="h-summary" className="eyebrow">Summary</h2>
      <p style={{ fontSize: mobile ? 19 : 22, lineHeight: 1.55, margin: 0, color: "var(--ink-2)", fontWeight: 500 }} data-testid="proposal-summary">{p.summary}</p>
    </section>
    <section aria-labelledby="h-story" style={{ marginBottom: 48 }}>
      <h2 id="h-story" className="eyebrow">The story</h2>
      <StoryHtml html={p.story} testId="proposal-story" />
    </section>
    {p.gallery.length > 0 && (
      <section aria-labelledby="h-more">
        <h2 id="h-more" className="head" style={{ fontSize: 26, margin: "0 0 20px" }}>More images and videos</h2>
        <Gallery items={p.gallery} />
      </section>
    )}
  </article>
);

const Body = ({ p, v, mobile }) => {
  const flat = mobile && p.header_type !== "slider";
  return (
    <div className="wrap-md" style={{ paddingBottom: mobile ? 120 : 72 }}>
      {!mobile && <Crumbs p={p} />}
      <div style={mediaWrap(mobile, flat)}>
        <HeaderMedia p={p} ratio={mobile ? "4 / 3" : undefined} flat={flat} />
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 48, alignItems: "flex-start", marginTop: mobile ? 20 : 32 }}>
        <Article p={p} mobile={mobile} />
        <Aside p={p} v={v} mobile={mobile} />
      </div>
    </div>
  );
};

const mediaWrap = (mobile, flat) => {
  if (!mobile) return undefined;
  return flat ? { margin: "0 -16px" } : { marginTop: 16 };
};

const NotFound = () => (
  <div className="wrap-md" style={{ padding: "80px 24px" }} data-testid="proposal-not-found">
    <h1 className="head" style={{ fontSize: 36 }}>We couldn’t find that proposal</h1>
    <p className="hint" style={{ fontSize: 16 }}>It may have been removed, or the link may be wrong.</p>
    <Link to="/" className="btn btn-primary" style={{ marginTop: 20 }}>Explore the map</Link>
  </div>
);

const Loaded = ({ p }) => {
  const mobile = useIsMobile();
  const v = useVote(p);
  const shown = useMemo(() => ({ ...p, pct: v.pct }), [p, v.pct]);
  return (
    <>
      {p.preview && <PreviewBanner p={p} />}
      <Body p={shown} v={v} mobile={mobile} />
      {mobile && !p.preview && <MobileVoteBar v={v} />}
    </>
  );
};

export default function ProposalPage() {
  const { slug } = useParams();
  const [p, setP] = useState(null);
  const [error, setError] = useState("");
  const mobile = useIsMobile();

  useEffect(() => {
    let live = true;
    setP(null);
    setError("");
    loadProposal(slug).then((data) => {
      if (!live) return;
      setP(data);
      document.title = `${data.title} · ReimagineCork`;
    }).catch((e) => live && setError(errMessage(e)));
    return () => {
      live = false;
    };
  }, [slug]);

  return (
    <div style={{ minHeight: "100vh", display: "flex", flexDirection: "column", paddingBottom: mobile ? 92 : 0 }} data-testid="proposal-page">
      {mobile ? <MobileTop p={p} /> : <SiteHeader />}
      <main style={{ flex: 1 }}>
        {error && <NotFound />}
        {!error && (p ? <Loaded key={p.id} p={p} /> : <Loader />)}
      </main>
      <SiteFooter width="wrap-md" />
    </div>
  );
}
